import { FC, useEffect, useMemo } from "react"
import { HeadFC, PageProps, graphql } from "gatsby"
import { useDispatch, useSelector } from "react-redux"

// components
import { HeadUniversal } from "../../components/HeadUniversal/HeadUniversal"
import {
  SearchResultsMicroCopy,
  SearchResultsTemplate
} from "../../components/SearchResultsTemplate/SearchResultsTemplate"
import { HrefLangTags } from "../../components/HrefLangTags/HrefLangTags"

// data
import { staticHrefLangTags } from "../../utils/seo/href_lang_tag_data"

// utils
import {
  generateDescriptionMetaTag,
  generateRobotsMetaTag,
  getTitleText
} from "../../utils/seo/seo_utils"
import { events } from "../../utils/heap/tracking_events"
import { assignLangCode } from "../../utils/general_utils"

// types
import { RootState } from "../../store/store"
import { CanonicalTag } from "../../components/CanonicalTag/CanonicalTag"

export type PageContext = {
  id: string
  node_locale: string
}

export const Search: FC<PageProps<Queries.SearchResultsTemplateDataQuery>> = (
  props
) => {
  const data = props?.data
  const userProfile = useSelector((state: RootState) => state.userProfile)

  const currentLangCode = useSelector(
    (state: RootState) => state.metaData.langCode
  )

  const dispatch = useDispatch()

  const searchResultsMicropySet = useMemo<SearchResultsMicroCopy>(() => {
    return {
      filterSearchAllResults: {
        raw: data?.filterSearchAllResults?.microcopyValue?.raw ?? ""
      },
      filterSearchAttendeeTopics: {
        raw: data?.filterSearchAttendeeTopics?.microcopyValue?.raw ?? ""
      },
      filterSearchOrganizerTopics: {
        raw: data?.filterSearchOrganizerTopics?.microcopyValue?.raw ?? ""
      },
      headerNoSearchTermEntered: {
        raw: data?.headerNoSearchTermEntered?.microcopyValue?.raw ?? ""
      },
      headerSearchResults: {
        raw: data?.headerSearchResults?.microcopyValue?.raw ?? ""
      },
      headerNoSearchResults: {
        raw: data?.headerNoSearchResults?.microcopyValue?.raw ?? ""
      },
      messageSearchBar: {
        raw: data?.messageSearchBar?.microcopyValue?.raw ?? ""
      },
      messageNoSearchResultsSubheader: {
        raw: data?.messageNoSearchResultsSubheader?.microcopyValue?.raw ?? ""
      },
      messageSearchResultsLoading: {
        raw: data?.messageSearchResultsLoading?.microcopyValue?.raw ?? ""
      },
      buttonBrowseHelpCenter: {
        raw: data?.buttonBrowseHelpCenter?.microcopyValue?.raw ?? ""
      }
    }
  }, [data])

  useEffect(() => {
    assignLangCode(currentLangCode, dispatch)
  }, [])

  useEffect(() => {
    if (userProfile) {
      events?.userProfileData("User Profile", userProfile)
    }
  }, [userProfile])

  return (
    <SearchResultsTemplate
      breadcrumbMicroCopy={data?.navigationHomepage}
      gatsbyPageProps={props}
      layoutProps={{
        microCopy: {
          buttonBrowseHelpCenter: data?.buttonBrowseHelpCenter,
          buttonContactUs: data?.buttonContactUs,
          messageContactUs: data?.messageContactUs,
          upperNavMicroCopySet: data?.upperNavMicroCopySet,
          headerSearchResults: data?.headerSearchResults,
          messageSearchBar: data?.messageSearchBar,
          chatbotUITranslations: data?.chatbotUITranslations
        },
        footerCopy: data?.contentfulFooter?.footerReference ?? null,
        footerNavCopy:
          data?.contentfulFooterNavigation?.FooterNavigationChildItems ?? null
      }}
      microCopy={searchResultsMicropySet}
      {...props}
      params={{ lang_code: currentLangCode }}
    />
  )
}

export const Head: HeadFC<
  Queries.SearchResultsTemplateDataQuery,
  PageContext
> = (headArgs) => {
  const canonicalUrl = useMemo<string>(() => {
    if (headArgs?.location?.pathname?.length > 0) {
      return headArgs?.location?.pathname
    }

    return ""
  }, [headArgs])

  const shouldNoIndex = useMemo(
    () => !headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagRobotsIndex,
    [headArgs?.data?.contentfulSeoMetaTags]
  )

  return (
    <>
      <HeadUniversal {...headArgs} 
      metaTitle={headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagTitle}      
      metaDescription={headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagDescription}
      />
      
      {headArgs?.data?.contentfulSeoMetaTags && (
        <>
          {getTitleText(
            headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagTitle ?? ""
          )}
          {generateDescriptionMetaTag(
            headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagDescription ?? ""
          )}
          {generateRobotsMetaTag(
            headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagRobotsIndex ??
              false,
            headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagCrawlerFollow ??
              false
          )}
          <HrefLangTags
            metaData={staticHrefLangTags("/search")}
            shouldNoIndex={shouldNoIndex}
          />
          )
          <CanonicalTag
            href={canonicalUrl}
            nodeLocale={headArgs?.pageContext?.node_locale}
            shouldNoIndex={shouldNoIndex}
          />
          )
        </>
      )}
    </>
  )
}

export const query = graphql`
  query SearchResultsTemplateData($node_locale: String!) {
    chatbotUITranslations: allContentfulMicrocopySet(
      filter: {
        microcopySetInternalName: { eq: "Chatbot UI" }
        node_locale: { eq: $node_locale }
      }
    ) {
      edges {
        node {
          node_locale
          microcopySetEntries {
            ... on ContentfulMicrocopy {
              contentful_id
              microcopyKey
              microcopyValue {
                raw
              }
            }
          }
        }
      }
    }

    allContentfulCategoryMenu(filter: { node_locale: { eq: $node_locale } }) {
      nodes {
        contentful_id
        categoryTitle
        categoryAudience {
          contentful_id
          english
        }
      }
      totalCount
    }
    contentfulSeoMetaTags(
      seoMetaTagInternalTitle: { eq: "searchResultsPageSeo" }
      node_locale: { eq: $node_locale }
    ) {
      seoMetaTagCrawlerFollow
      seoMetaTagDescription
      seoMetaTagRobotsIndex
      seoMetaTagTitle
    }

    # BREADCRUMB FIELDS
    navigationHomepage: contentfulMicrocopy(
      microcopyKey: { eq: "navigationHomepage" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    # END BREADCRUMB FIELDS

    contentfulFooter(node_locale: { eq: $node_locale }) {
      id
      footerInternalName
      footerReference {
        displayText
        FooterNavigationChildItems {
          footerNavigationInternalTitle
          displayText
          footerNavigationUrl
        }
      }
    }
    contentfulFooterNavigation(
      footerNavigationInternalTitle: { eq: "Category: Bottom row navigation" }
      node_locale: { eq: $node_locale }
    ) {
      footerNavigationInternalTitle
      footerNavigationUrl
      FooterNavigationChildItems {
        footerNavigationInternalTitle
        displayText
        footerNavigationUrl
      }
    }

    upperNavMicroCopySet: contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Help Center nav" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    messageContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "messageStillHaveQuestions" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    buttonContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "buttonContactUs" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    buttonBrowseHelpCenter: contentfulMicrocopy(
      microcopyKey: { eq: "buttonBrowseHelpCenter" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    messageSearchBar: contentfulMicrocopy(
      microcopyKey: { eq: "messageSearchBar" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    messageSearchResultsLoading: contentfulMicrocopy(
      microcopyKey: { eq: "searchResultsLoading" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerSearchResults: contentfulMicrocopy(
      microcopyKey: { eq: "headerSearchResults" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerNoSearchResults: contentfulMicrocopy(
      microcopyKey: { eq: "headerNoSearchResults" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerNoSearchTermEntered: contentfulMicrocopy(
      microcopyKey: { eq: "headerNoSearchTermEntered" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    filterSearchAllResults: contentfulMicrocopy(
      microcopyKey: { eq: "filterSearchAllResults" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    filterSearchAttendeeTopics: contentfulMicrocopy(
      microcopyKey: { eq: "filterSearchAttendeeTopics" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    filterSearchOrganizerTopics: contentfulMicrocopy(
      microcopyKey: { eq: "filterSearchOrganizerTopics" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    messageNoSearchResultsSubheader: contentfulMicrocopy(
      microcopyKey: { eq: "messageNoSearchResultsSubheader" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
  }
`

export default Search
